import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { TAG_COLORS, TagColorKey, getNextTagColor, tagNameToSlug } from '../../utils/tagColors';

export interface Tag {
  id: string;
  name: string;
  slug: string;
  color: TagColorKey;
  ai_description?: string;
  ai_instructions?: string;
}

interface TagInputProps {
  tags: Tag[];
  onChange: (tags: Tag[]) => void;
  availableTags?: Tag[]; // Existing tags for autocomplete
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
}

export function TagInput({
  tags,
  onChange,
  availableTags = [],
  placeholder = 'Add tags...',
  disabled = false,
  maxTags,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input
  const suggestions = availableTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.some((t) => t.slug === tag.slug)
  );

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (tagToAdd: Tag | string) => {
    if (maxTags && tags.length >= maxTags) return;

    let newTag: Tag;

    if (typeof tagToAdd === 'string') {
      const name = tagToAdd.trim();
      if (!name) return;

      const slug = tagNameToSlug(name);

      // Check if tag already exists
      if (tags.some((t) => t.slug === slug)) {
        setInputValue('');
        return;
      }

      // Check if it's an existing tag from availableTags
      const existingTag = availableTags.find((t) => t.slug === slug);
      if (existingTag) {
        newTag = existingTag;
      } else {
        // Create new tag with auto-assigned color
        const usedColors = [...tags, ...availableTags].map((t) => t.color);
        newTag = {
          id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name,
          slug,
          color: getNextTagColor(usedColors),
        };
      }
    } else {
      // Tag object passed directly
      if (tags.some((t) => t.slug === tagToAdd.slug)) {
        setInputValue('');
        return;
      }
      newTag = tagToAdd;
    }

    onChange([...tags, newTag]);
    setInputValue('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(0);
  };

  const removeTag = (tagToRemove: Tag) => {
    onChange(tags.filter((t) => t.id !== tagToRemove.id));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();

      if (showSuggestions && suggestions.length > 0) {
        // Add selected suggestion
        addTag(suggestions[selectedSuggestionIndex]);
      } else if (inputValue.trim()) {
        // Add new tag from input
        addTag(inputValue);
      }
    } else if (e.key === ' ' && inputValue.trim()) {
      // Space adds tag only if there's content
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace when input is empty
      removeTag(tags[tags.length - 1]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (value: string) => {
    // Remove commas from input
    const cleanValue = value.replace(/,/g, '');
    setInputValue(cleanValue);
    setShowSuggestions(cleanValue.length > 0);
    setSelectedSuggestionIndex(0);
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex flex-wrap gap-2 p-2 min-h-[42px] border rounded-lg bg-white dark:bg-gray-800 transition-colors ${
          disabled
            ? 'border-gray-200 dark:border-gray-700 opacity-60'
            : 'border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-ondeck-500 focus-within:border-ondeck-500'
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Tag Chips */}
        {tags.map((tag) => (
          <TagChip key={tag.id} tag={tag} onRemove={() => removeTag(tag)} disabled={disabled} />
        ))}

        {/* Input */}
        {(!maxTags || tags.length < maxTags) && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue && setShowSuggestions(true)}
            placeholder={tags.length === 0 ? placeholder : ''}
            disabled={disabled}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => addTag(suggestion)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                index === selectedSuggestionIndex
                  ? 'bg-ondeck-50 dark:bg-ondeck-900/30'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <span
                className={`w-3 h-3 rounded-full ${TAG_COLORS[suggestion.color].dot}`}
              />
              <span className="text-gray-900 dark:text-white flex-1">{suggestion.name}</span>
              {hasAiContext(suggestion) && (
                <span title="Has AI context">
                  <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Create new tag hint */}
      {showSuggestions && inputValue && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">Enter</kbd> to create "<span className="text-gray-900 dark:text-white font-medium">{inputValue}</span>"
          </p>
        </div>
      )}
    </div>
  );
}

// Helper to check if tag has AI context
function hasAiContext(tag: Tag): boolean {
  return !!(tag.ai_description?.trim() || tag.ai_instructions?.trim());
}

// Individual Tag Chip
function TagChip({
  tag,
  onRemove,
  disabled,
}: {
  tag: Tag;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const colors = TAG_COLORS[tag.color] || TAG_COLORS.gray;
  const hasAi = hasAiContext(tag);

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border`}
      title={hasAi ? 'This tag includes AI context' : undefined}
    >
      <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
      {tag.name}
      {hasAi && (
        <svg className="w-3 h-3 opacity-70" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 hover:opacity-70 transition-opacity"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
}

// Simple display-only tag chip for lists
export function TagBadge({ tag }: { tag: Tag }) {
  const colors = TAG_COLORS[tag.color] || TAG_COLORS.gray;
  const hasAi = hasAiContext(tag);

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}
      title={hasAi ? 'This tag includes AI context' : undefined}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
      {tag.name}
      {hasAi && (
        <svg className="w-2.5 h-2.5 opacity-70" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </span>
  );
}
